import { useState } from 'react';

import SyncIcon from '@mui/icons-material/Sync';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { useFormik } from 'formik';
import { Button as ActionButton, Confirm, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { AddressDto } from 'src/dtos';
import { ErrorMessageAlert } from 'src/lib';
import { Account } from 'src/models';
import { Spacing } from 'src/styles';
import * as yup from 'yup';

const SyncAccountDescription = () => {
  return (
    <Grid item sm={12}>
      <Typography variant='body2' color='textSecondary'>
        Synchronizing account information will retrieve the latest account details from the TradingBlock to My IPO.
        Please note that under the following circumstances the synchronization will fail:
      </Typography>
      <List dense>
        <ListItem>
          <Typography variant='body2' color='textSecondary'>
            TradingBlock primary account holder email address does not match the email address of the primary account
            holder in My IPO.
          </Typography>
        </ListItem>
        <ListItem>
          <Typography variant='body2' color='textSecondary'>
            TradingBlock account does not have account number.
          </Typography>
        </ListItem>
        <ListItem>
          <Typography variant='body2' color='textSecondary'>
            TradingBlock account type does not match My IPO account type.
          </Typography>
        </ListItem>
      </List>
    </Grid>
  );
};

interface SyncEntityAccountFormValues {
  name: string;
  taxId: string;
  address: AddressDto & { country: string };
  phoneNumber: string;
}

const validationSchema = yup.object().shape({
  name: yup.string().required('Entity Name is required'),
  taxId: yup.string().required('Tax ID is required'),
  address: yup.object().shape({
    address1: yup.string().required('Address Line 1 is required'),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    postalCode: yup.string().required('Postal Code is required'),
    country: yup.string().required('Country is required'),
  }),
  phoneNumber: yup.string(),
});

interface SyncEntityAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SyncEntityAccountModal = ({ isOpen, onClose }: SyncEntityAccountModalProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const account = useRecordContext<Account>();

  const [update, { isLoading, error, isError }] = useUpdate();

  const onSubmit = async (values: SyncEntityAccountFormValues) => {
    update(
      DataProviderResource.SyncAccount,
      {
        id: account.id,
        data: {
          entityInformation: values,
        },
      },
      {
        onSuccess: () => {
          notify('Account was successfully synchronized.', { type: 'success' });
          onClose();
          refresh();
        },
      },
    );
  };

  const formik = useFormik<SyncEntityAccountFormValues>({
    initialValues: {
      name: account.entityInformation?.name ?? '',
      taxId: account.entityInformation?.taxId ?? '',
      address: {
        address1: account.entityInformation?.address.address1 ?? '',
        address2: account.entityInformation?.address.address2 ?? '',
        city: account.entityInformation?.address.city ?? '',
        state: account.entityInformation?.address.state?.value ?? '',
        postalCode: account.entityInformation?.address.postalCode ?? '',
        country: account.entityInformation?.address.country?.value ?? 'USA',
      },
      phoneNumber: account.entityInformation?.phoneNumber ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit,
  });

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth>
      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <DialogTitle>Sync Entity Account</DialogTitle>
        <DialogContent>
          <SyncAccountDescription />
          <Grid container direction='column' spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name='name'
                label='Entity Name'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.name)}
                helperText={formik.errors.name}
                value={formik.values.name}
                onChange={formik.handleChange}
                fullWidth
                sx={{ width: '50%' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='taxId'
                label='Tax ID'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.taxId)}
                helperText={formik.errors.taxId}
                value={formik.values.taxId}
                onChange={formik.handleChange}
                fullWidth
                sx={{ width: '50%' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='phoneNumber'
                label='Phone Number(s)'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.phoneNumber)}
                helperText={formik.errors.phoneNumber}
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                fullWidth
                sx={{ width: '50%' }}
              />
            </Grid>
            <Grid item xs={12} md={6} className={Spacing.mt32}>
              <Typography variant='h6'>Address</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='address1'
                label='Address Line 1'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.address?.address1)}
                helperText={formik.errors.address?.address1}
                value={formik.values.address.address1}
                onChange={event => formik.setFieldValue('address.address1', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='address2'
                label='Address Line 2'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.address?.address2)}
                helperText={formik.errors.address?.address2}
                value={formik.values.address.address2}
                onChange={event => formik.setFieldValue('address.address2', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='city'
                label='City'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.address?.city)}
                helperText={formik.errors.address?.city}
                value={formik.values.address.city}
                onChange={event => formik.setFieldValue('address.city', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='state'
                label='State'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.address?.state)}
                helperText={formik.errors.address?.state}
                value={formik.values.address.state}
                onChange={event => formik.setFieldValue('address.state', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='postalCode'
                label='Postal Code'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.address?.postalCode)}
                helperText={formik.errors.address?.postalCode}
                value={formik.values.address.postalCode}
                onChange={event => formik.setFieldValue('address.postalCode', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name='country'
                label='Country'
                variant='standard'
                margin='dense'
                type='text'
                disabled={isLoading}
                error={Boolean(formik.errors.address?.country)}
                helperText={formik.errors.address?.country}
                value={formik.values.address.country}
                onChange={event => formik.setFieldValue('address.country', event.target.value)}
                fullWidth
              />
            </Grid>
            {isError && (
              <Grid item xs={12} md={6}>
                <ErrorMessageAlert error={error} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
            <Grid item>
              <Button variant='outlined' color='inherit' size='medium' disabled={isLoading} onClick={onClose}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                size='medium'
                disabled={isLoading || !formik.isValid}
                type='submit'>
                Save
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export const SyncAccountButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const account = useRecordContext<Account>();

  const [update, { isLoading }] = useUpdate();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isEntitySyncOpen, setIsEntitySyncOpen] = useState(false);

  const onSync = async () => {
    update(
      DataProviderResource.SyncAccount,
      {
        id: account.id,
      },
      {
        onSuccess: () => {
          notify('Account was successfully synchronized.', { type: 'success' });
          setIsConfirmOpen(false);
        },
        onSettled: () => {
          setIsConfirmOpen(false);
          refresh();
        },
        onError: err => {
          notify(`Account synchronization error: ${err}`, {
            type: 'error',
            autoHideDuration: 10000,
          });
        },
      },
    );
  };

  const renderConfirmContent = () => {
    return (
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Typography variant='body1'>Are you sure you want to synchronize account information?</Typography>
        </Grid>
        <SyncAccountDescription />
      </Grid>
    );
  };

  return (
    <>
      <ActionButton
        label='Sync'
        onClick={() => {
          if (account.type.isEntity) {
            setIsEntitySyncOpen(true);

            return;
          }
          setIsConfirmOpen(true);
        }}
        disabled={isLoading || !account.accountId}>
        <SyncIcon />
      </ActionButton>
      <Confirm
        isOpen={isConfirmOpen}
        loading={isLoading}
        title={'Sync Account'}
        content={renderConfirmContent()}
        onConfirm={() => {
          onSync();
        }}
        onClose={() => setIsConfirmOpen(false)}
      />
      <SyncEntityAccountModal
        isOpen={isEntitySyncOpen}
        onClose={() => {
          setIsEntitySyncOpen(false);
        }}
      />
    </>
  );
};
